import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

interface Tab {
  label: string;
  path: string;
  fullPath: string;  // Store full path including parameters and query params
  filterData: any;
  params?: any; // Added to support optional parameters
}

@Injectable({
  providedIn: 'root'
})
export class TabService {
  private storageKey = 'tabs';
  private tabsSubject: BehaviorSubject<Tab[]>;
  private activeTabSubject: BehaviorSubject<number>;

  tabs$: any;
  activeTab$: any;

  constructor() {
    const savedTabs = localStorage.getItem(this.storageKey);
    const initialTabs = savedTabs ? JSON.parse(savedTabs) : [];
    this.tabsSubject = new BehaviorSubject<Tab[]>(initialTabs);

    const savedActiveTab = localStorage.getItem(this.storageKey + '_active');
    const storedIndex = savedActiveTab ? Number(savedActiveTab) : 0;
    const initialActiveTab = Number.isInteger(storedIndex)
      ? Math.max(0, Math.min(storedIndex, initialTabs.length - 1)) : 0;
    this.activeTabSubject = new BehaviorSubject<number>(initialActiveTab);

    this.tabs$ = this.tabsSubject.asObservable();
    this.activeTab$ = this.activeTabSubject.asObservable();
    this.saveActiveTab();
  }

  private saveTabs() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.tabsSubject.value));
  }

  private saveActiveTab() {
    localStorage.setItem(this.storageKey + '_active', this.activeTabSubject.value.toString());
  }

  get tabs(): Tab[] {
    return this.tabsSubject.value;
  }

  get activeTab(): number {
    return this.activeTabSubject.value;
  }

  addTab(tab: Tab) {
    // Generate the full path including parameters if provided
    const pathWithParams = tab.params
      ? `${tab.path}?${new URLSearchParams(tab.params).toString()}`
      : tab.path;

    tab.fullPath = pathWithParams; // Ensure fullPath reflects the path and params

    const existingTab = this.tabs.findIndex(t => {
      if (t?.path.includes('receiving-services/asn') && tab?.path.includes('receiving-services/asn')) {
        t.fullPath = pathWithParams; // Update fullPath if similar tab exists
      }
      if (t?.path.includes('receiving-services/putaway') && tab?.path.includes('receiving-services/putaway')) {
        t.fullPath = pathWithParams;
      }
      if (t?.path.includes('receiving-services/grn') && tab?.path.includes('receiving-services/grn')) {
        t.fullPath = pathWithParams;
      }
      if (t?.path.includes('order-services/picklist') && tab?.path.includes('order-services/picklist')) {
        t.fullPath = pathWithParams;
      }
      if (t?.path.includes('order-services/order') && tab?.path.includes('order-services/order')) {
        t.fullPath = pathWithParams;
      }
      if (t?.path.includes('order-services/manifest') && tab?.path.includes('order-services/manifest')) {
        t.fullPath = pathWithParams;
      }
      if (t?.path.includes('utility/upload') && tab?.path.includes('utility/upload')) {
        t.fullPath = pathWithParams;
      }
      if (t?.path.includes('ems-master/quick-code') && tab?.path.includes('ems-master/quick-code')) {
        t.fullPath = pathWithParams;
      }
      // Updated to match based on fullPath (path + params)
      return this.normalizeTabPath(t.fullPath || t.path) === this.normalizeTabPath(pathWithParams);
    });

    if (existingTab === -1) {
      this.tabsSubject.next([...this.tabs, { ...tab, filterData: this.copyFilters(tab.filterData) }]);
      this.setActiveTab(this.tabs.length - 1);
    } else {
      this.setActiveTab(existingTab);
    }
    this.saveTabs();
    this.saveActiveTab();
  }

  setActiveTab(index: number) {
    if (!Number.isInteger(index) || index < 0 || index >= this.tabs.length) return;
    this.activeTabSubject.next(index);
    this.saveActiveTab();
  }

  updateTab(index: number, tab: Tab) {
    if (!this.tabs[index]) return;
    const tabs = [...this.tabs];
    if (tabs.length > 0) {
      tabs[index] = tab;
      this.tabsSubject.next(tabs);
      this.saveTabs();
    }
  }

  closeTab(index: number) {
    if (!this.tabs[index]) return;
    const tabs = [...this.tabs];
    tabs.splice(index, 1);
    this.tabsSubject.next(tabs);
    this.saveTabs();
    if (!tabs.length) {
      this.activeTabSubject.next(0);
    } else if (this.activeTab === index) {
      if (tabs.length > 0) {
        this.setActiveTab(index === 0 ? 0 : index - 1);
      } else {
        this.setActiveTab(0);
      }
    } else if (this.activeTab > index) {
      this.setActiveTab(this.activeTab - 1);
    }
    this.saveActiveTab();
  }

  closeAllTab() {
    const tabs: Tab[] = [];
    this.tabsSubject.next(tabs);
    this.saveTabs();
    this.activeTabSubject.next(0);
    this.saveActiveTab();
  }

  updateFilterData(index: number, filterData: any) {
    if (!this.tabs[index]) return;
    const tabs = [...this.tabs];
    if (tabs.length > 0) {
      tabs[index] = { ...tabs[index], filterData: this.copyFilters(filterData) };
      this.tabsSubject.next(tabs);
      this.saveTabs();
    }
  }

  /** Capture this from the component's route, never from the globally active tab. */
  getRoutePath(route: ActivatedRoute): string {
    const path = '/' + route.pathFromRoot.flatMap(part => part.snapshot.url.map(segment => segment.toString())).join('/');
    const query = new URLSearchParams();
    route.snapshot.queryParamMap.keys.forEach(key =>
      route.snapshot.queryParamMap.getAll(key).forEach(value => query.append(key, value)));
    return path + (query.toString() ? '?' + query.toString() : '');
  }

  findTabIndex(path: string): number {
    const key = this.normalizeTabPath(path);
    return this.tabs.findIndex(tab => this.normalizeTabPath(tab.fullPath || tab.path) === key);
  }

  getFilterDataForPath(path: string): any[] {
    return this.copyFilters(this.tabs[this.findTabIndex(path)]?.filterData);
  }

  updateFilterDataForPath(path: string, filterData: any): void {
    this.updateFilterData(this.findTabIndex(path), filterData);
  }

  private normalizeTabPath(path: string): string {
    const [base, query = ''] = path.split('#')[0].split('?');
    const params = new URLSearchParams(query);
    params.sort();
    return '/' + base.replace(/^\/+|\/+$/g, '') + (params.toString() ? '?' + params.toString() : '');
  }

  private copyFilters(filters: any): any[] {
    return Array.isArray(filters) ? JSON.parse(JSON.stringify(filters)) : [];
  }

  transformFiltersArrayToObject(filtersArray: any[]): any {
    return (filtersArray || []).reduce((acc, curr) => {
      if (curr.value !== null && curr.value !== undefined) {
        acc[curr.key] = curr.value;
      }
      if (curr.dateTimeValue) {
        acc[curr.key] = curr.dateTimeValue;
      }
      if (curr.dateValue) {
        acc[curr.key] = curr.dateValue;
      }
      return acc;
    }, {});
  }
}
